// src/context/ReadOnlyContext.jsx
// Context for managing read-only mode throughout the application
// This supports both global viewer role and per-analysis read-only permissions

import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { userService } from '../services/apiServices';
import { useTranslation } from 'react-i18next';

// Create the ReadOnly context
const ReadOnlyContext = createContext({
    isReadOnly: false,
    canEdit: true,
    isViewer: false,
    readOnlyMessage: '',
    checkAnalysisPermission: () => false,
    refreshPermissions: () => { }
});

// We'll get the analysis ID from localStorage since AnalysisContext might not be available
const getCurrentAnalysisId = () => {
    try {
        return localStorage.getItem('currentAnalysisId');
    } catch {
        return null;
    }
};

/**
 * ReadOnly Provider Component
 * Wraps the application to provide read-only state based on user role and per-analysis permissions
 */
export const ReadOnlyProvider = ({ children }) => {
    const { isViewer, user } = useAuth();
    const { t } = useTranslation();
    const [analysisPermissions, setAnalysisPermissions] = useState({});
    const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
    const [currentAnalysisId, setCurrentAnalysisId] = useState(getCurrentAnalysisId());

    // Listen for analysis changes in localStorage
    useEffect(() => {
        const handleStorageChange = () => {
            setCurrentAnalysisId(getCurrentAnalysisId());
        };

        // Check periodically for changes (since storage events don't fire in same tab)
        const interval = setInterval(handleStorageChange, 1000);

        window.addEventListener('storage', handleStorageChange);
        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Load user's analysis permissions
    const loadPermissions = useCallback(async () => {
        const userId = user?.id || user?._id;
        console.log('[READONLY_DEBUG] loadPermissions called with user:', userId, 'role:', user?.role);

        if (!user || !userId) {
            console.log('[READONLY_DEBUG] No user, skipping permission load');
            return;
        }

        // For admin/superadmin, no need to load permissions - they have full access
        if (user.role === 'admin' || user.role === 'superadmin') {
            console.log('[READONLY_DEBUG] Admin/Superadmin has full access, skipping permission load');
            return;
        }

        try {
            setIsLoadingPermissions(true);
            // Get the user's current data with authorizedAnalyses
            const response = await userService.getUserById(userId);

            if (response.success && response.data?.authorizedAnalyses) {
                const perms = {};
                response.data.authorizedAnalyses.forEach(auth => {
                    // Check for new format FIRST (object with analysisId and permission)
                    if (auth.analysisId) {
                        // New format: { analysisId, permission }
                        const analysisId = typeof auth.analysisId === 'object'
                            ? (auth.analysisId._id || auth.analysisId.toString())
                            : auth.analysisId;
                        perms[analysisId] = auth.permission || 'read-write';
                    } else if (typeof auth === 'string') {
                        // Old format: just string ID
                        perms[auth] = 'read-write';
                    } else if (auth._id) {
                        // Old format: ObjectId
                        const analysisId = auth._id.toString ? auth._id.toString() : auth._id;
                        perms[analysisId] = 'read-write';
                    }
                });
                console.log('[READONLY_DEBUG] Loaded permissions:', perms);
                setAnalysisPermissions(perms);
            }
        } catch (error) {
            console.error('Error loading analysis permissions:', error);
        } finally {
            setIsLoadingPermissions(false);
        }
    }, [user]);

    // Load permissions when user changes
    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    /**
     * Check if user has read-only access to a specific analysis
     * @param {string} analysisId - The analysis ID to check
     * @returns {boolean} - True if read-only, false if read-write or no access
     */
    const checkAnalysisPermission = useCallback((analysisId) => {
        // Global viewer role always means read-only
        if (isViewer) return true;

        // Admin/superadmin always have full access
        if (user?.role === 'admin' || user?.role === 'superadmin') {
            return false;
        }

        // Check per-analysis permission
        if (analysisId && analysisPermissions[analysisId]) {
            return analysisPermissions[analysisId] === 'read-only';
        }

        // Default to editable if no specific permission found
        // (the user might be the owner, which is handled by backend)
        return false;
    }, [isViewer, user?.role, analysisPermissions]);

    // Determine if current analysis is read-only
    const isCurrentAnalysisReadOnly = useMemo(() => {
        let result = false;
        let reason = '';

        if (isViewer) {
            result = true;
            reason = 'Global viewer role';
        } else if (user?.role === 'admin' || user?.role === 'superadmin') {
            result = false;
            reason = 'Admin/Superadmin has full access';
        } else if (currentAnalysisId) {
            result = checkAnalysisPermission(currentAnalysisId);
            reason = `Per-analysis permission for ${currentAnalysisId}: ${analysisPermissions[currentAnalysisId] || 'not found'}`;
        } else {
            result = false;
            reason = 'No current analysis ID';
        }

        console.log(`[READONLY_DEBUG] isCurrentAnalysisReadOnly = ${result} (${reason})`);
        return result;
    }, [isViewer, user?.role, currentAnalysisId, checkAnalysisPermission, analysisPermissions]);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        // True if the user should not be able to edit (global or per-analysis)
        isReadOnly: isViewer === true || isCurrentAnalysisReadOnly,
        // Convenience property - inverse of isReadOnly
        canEdit: isViewer !== true && !isCurrentAnalysisReadOnly,
        // Message to show when user tries to edit
        readOnlyMessage: isViewer
            ? t('common.readOnlyMessageGlobal', 'Vous êtes en mode lecture seule. Vous ne pouvez pas modifier les données.')
            : isCurrentAnalysisReadOnly
                ? t('common.readOnlyMessageAnalysis', 'Vous avez un accès en lecture seule à cette analyse.')
                : '',
        // The user's role for reference
        userRole: user?.role || null,
        // Global viewer flag (consumers destructure it directly)
        isViewer: isViewer === true,
        // Function to check if specific analysis is read-only
        checkAnalysisPermission,
        // Function to refresh permissions
        refreshPermissions: loadPermissions,
        // Whether permissions are loading
        isLoadingPermissions,
        // Current analysis ID being checked
        currentAnalysisId,
        // Raw permissions map for debugging
        analysisPermissions
    }), [isViewer, isCurrentAnalysisReadOnly, user?.role, checkAnalysisPermission, loadPermissions, isLoadingPermissions, currentAnalysisId, analysisPermissions, t]);

    return (
        <ReadOnlyContext.Provider value={contextValue}>
            {children}
        </ReadOnlyContext.Provider>
    );
};

/**
 * Hook to access read-only state
 * @returns {Object} ReadOnly context value
 * - isReadOnly: boolean - true if user cannot edit
 * - canEdit: boolean - true if user can edit
 * - readOnlyMessage: string - message to display when in read-only mode
 * - checkAnalysisPermission: function - check if specific analysis is read-only
 */
export const useReadOnly = () => {
    const context = useContext(ReadOnlyContext);

    if (!context) {
        // If used outside provider, default to allowing edits
        console.warn('useReadOnly must be used within a ReadOnlyProvider. Defaulting to editable mode.');
        return {
            isReadOnly: false,
            canEdit: true,
            isViewer: false,
            readOnlyMessage: '',
            checkAnalysisPermission: () => false,
            refreshPermissions: () => { }
        };
    }

    return context;
};

/**
 * Higher-Order Component to wrap components with read-only awareness
 * @param {Component} WrappedComponent - The component to wrap
 * @returns {Component} - The wrapped component with readOnly props
 */
export const withReadOnly = (WrappedComponent) => {
    return function ReadOnlyAwareComponent(props) {
        const readOnlyState = useReadOnly();
        return <WrappedComponent {...props} {...readOnlyState} />;
    };
};

/**
 * Utility component that shows a read-only banner when applicable
 */
export const ReadOnlyBanner = ({ className = '' }) => {
    const { isReadOnly, readOnlyMessage } = useReadOnly();
    const { t } = useTranslation();

    if (!isReadOnly) return null;

    return (
        <div
            className={`read-only-banner ${className}`}
            style={{
                background: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)', // Red gradient
                color: 'white',
                padding: '6px 20px', // Thinner padding
                textAlign: 'center',
                fontSize: '13px', // Slightly smaller font
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                position: 'sticky', // Makes it sticky
                top: 0, // Sticks to the top
                zIndex: 1000, // Ensures it stays on top of other content
                width: '100%' // Full width
            }}
        >
            <span>{readOnlyMessage}</span>
            <span style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '1px 6px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600'
            }}>
                {t('common.readOnlyMode', 'MODE LECTURE')}
            </span>
        </div>
    );
};

/**
 * Wrapper component that disables all interactive elements inside
 * Use this to quickly make a section read-only
 */
export const ReadOnlyWrapper = ({ children, showOverlay = false }) => {
    const { isReadOnly } = useReadOnly();

    if (!isReadOnly) return children;

    return (
        <div
            style={{
                position: 'relative',
                pointerEvents: 'none',
                opacity: showOverlay ? 0.7 : 1
            }}
        >
            {children}
            {showOverlay && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255,255,255,0.1)',
                        zIndex: 10
                    }}
                />
            )}
        </div>
    );
};

export default ReadOnlyContext;
